/**
 * User-facing phrasing for upload failures and attribute format preflight
 * (DV-570). Pure helpers — no GraphQL/Apollo — co-located with UploadDocumentModal.
 */

/** Empty-file rejection — matches the backend create validation wording. */
export const EMPTY_FILE_MESSAGE = "File size must be greater than zero";

/**
 * Shown if a file too large to batch reaches the batch API anyway. The picker
 * rejects these up front (DV-774), so this is the net for a client/DSS threshold
 * disagreement — without it the raw `batch create is single-part only: size_bytes
 * N requires a multipart upload` lands in the banner.
 */
export const BATCH_TOO_LARGE_MESSAGE =
  "This file is too large to upload with others. Add it on its own.";

export const INVALID_NUMBER_MESSAGE = "Enter a valid number";

/** Backend `Date` scalar / `date.fromisoformat` only accept calendar days. */
export const INVALID_DATE_MESSAGE = "Enter a date as YYYY-MM-DD";

/** Decimal-shaped strings the GraphQL `Decimal` scalar will accept. */
const DECIMAL_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** Calendar day `YYYY-MM-DD` (what `date.fromisoformat` accepts). */
const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

function isGregorianLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

/**
 * Date/Number scalars reject surrounding whitespace; trim those so preflight and
 * the wire value stay aligned. Text and other types keep the form value verbatim.
 */
export function wireAttributeValue(dataType: string, value: string): string {
  return dataType === "Date" || dataType === "Number" ? value.trim() : value;
}

/**
 * True when a form value carries nothing meaningful. Whitespace-only counts as
 * empty for every data type (DV-589) — spaces must not satisfy a required
 * attribute, nor be filed as a value. The single place readiness, the required
 * check, and the wire mapping agree on "this field is blank".
 */
export function isBlankAttributeValue(value: string | null | undefined): boolean {
  return !value || !value.trim();
}

export function isValidDecimalString(value: string): boolean {
  const trimmed = value.trim();
  // Match the Decimal wire shape only — do not coerce through JS `Number`
  // (IEEE-754 rejects magnitudes like `1e309` that GraphQL Decimal accepts).
  return !!trimmed && DECIMAL_RE.test(trimmed);
}

/**
 * True for a real Gregorian calendar day that Python `date.fromisoformat`
 * accepts. Avoids `Date.UTC` (JS maps years `0`–`99` → `1900`–`1999`). Does not
 * trim — the wire value must already be exact `YYYY-MM-DD` (year ≥ 1).
 */
export function isValidIsoDateString(value: string): boolean {
  const match = ISO_DAY_RE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // datetime.date rejects year 0; fromisoformat matches that.
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const maxDay = month === 2 && isGregorianLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1];
  return day <= maxDay;
}

/**
 * Why a typed attribute value cannot go on the wire. Whitespace-only is treated
 * as empty (caller's concern). Non-empty Date/Number values should already be
 * wire-shaped via `wireAttributeValue` before this runs.
 */
export function attributeValueFormatError(dataType: string, value: string): string | null {
  if (!value.trim()) return null;
  if (dataType === "Number" && !isValidDecimalString(value)) {
    return INVALID_NUMBER_MESSAGE;
  }
  if (dataType === "Date" && !isValidIsoDateString(value)) {
    return INVALID_DATE_MESSAGE;
  }
  return null;
}

/**
 * Rephrase a thrown upload/create failure for the takeover banner.
 * Prefer matching known server phrases over opaque GraphQL scalar errors.
 */
export function uploadFailureMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error);
  // Client empties are blocked by `isEmptyFile`; these cover the live DVB copy
  // plus older `size_bytes …` wording still seen on un-upgraded backends.
  if (/(size_bytes|File size) must be greater than zero/i.test(raw)) {
    return EMPTY_FILE_MESSAGE;
  }
  if (/single-part only|requires a multipart upload/i.test(raw)) {
    return BATCH_TOO_LARGE_MESSAGE;
  }
  // DVB#159 no longer appends publicRefId; keep the strip for older servers.
  if (/identical attribute values.*already exists/i.test(raw)) {
    return raw.replace(/\s*\(publicRefId\s+[^)]+\)\s*$/i, "").trim();
  }
  if (/Expected type\s*'?Decimal'?|valueNumber|Invalid Decimal/i.test(raw)) {
    return INVALID_NUMBER_MESSAGE;
  }
  if (/Expected type\s*'?Date'?|valueDate|fromisoformat|Invalid Date/i.test(raw)) {
    return INVALID_DATE_MESSAGE;
  }
  return raw;
}
